import { type ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { ChevronRight, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

interface CollapsibleSectionProps {
  id: string
  title: string
  isExpanded: boolean
  onToggle: (id: string) => void
  children: ReactNode
  rightContent?: ReactNode
  icon?: ReactNode
  className?: string
  /** "sidebar" = bordered list-section style (default); "inline" = lightweight for forms/modals */
  variant?: "sidebar" | "inline"
}

export function CollapsibleSection({
  id,
  title,
  isExpanded,
  onToggle,
  children,
  rightContent,
  icon,
  className = "",
  variant = "sidebar",
}: CollapsibleSectionProps) {
  if (variant === "inline") {
    return (
      <div className={className}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onToggle(id)}
            className={cn(
              "flex items-center gap-1.5 text-xs font-medium transition-colors duration-150",
              isExpanded ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <ChevronDown
              className={cn(
                "h-3 w-3 flex-shrink-0 transition-transform duration-200 ease-out",
                isExpanded ? "rotate-0" : "-rotate-90",
              )}
            />
            {icon}
            {title}
          </button>
          {rightContent && (
            <div
              className="flex-shrink-0 flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              {rightContent}
            </div>
          )}
        </div>
        {/* Grid row trick: transition grid-template-rows 0fr→1fr for smooth height with no JS measurement */}
        <div
          className="grid transition-[grid-template-rows] duration-200 ease-out"
          style={{ gridTemplateRows: isExpanded ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <div className="mt-2 rounded-md bg-muted/30 p-3">
              {children}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`border-b border-sidebar-border ${className}`}>
      <div className="py-1.5 border-b border-sidebar-border flex items-center gap-1 pr-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onToggle(id)}
          className="justify-start text-left h-5 px-1 hover:bg-sidebar-accent min-w-0 flex items-center gap-1"
        >
          {isExpanded ? (
            <ChevronDown className="h-3 w-3 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 flex-shrink-0" />
          )}
          {icon}
          <span className="text-xs font-semibold text-sidebar-foreground/70 uppercase tracking-wide truncate">
            {title}
          </span>
        </Button>
        {rightContent && (
          <div
            className="flex-shrink-0 flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {rightContent}
          </div>
        )}
      </div>
      {isExpanded && (
        <div className="p-1.5">
          {children}
        </div>
      )}
    </div>
  )
}

